import { Close, ExpandLess, ExpandMore } from '@mui/icons-material'
import {
  Box,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { HomepageLogo } from '..'
import type { WhoAmIPermissions } from '../../../../../types'
import { isNavDropdown, useNavConfig } from '../../config/useNavConfig'
import styles from './MobileNavMenu.module.scss'

interface MobileNavMenuProps {
  menuOpen: boolean
  onClose: () => void
  userPermissions: WhoAmIPermissions | null
}

const MobileNavMenu = ({
  menuOpen,
  onClose,
  userPermissions,
}: MobileNavMenuProps) => {
  const location = useLocation()
  const navConfig = useNavConfig(userPermissions)
  const [expandedItems, setExpandedItems] = useState<string[]>([])

  const toggleExpanded = (title: string) => {
    setExpandedItems((prev) =>
      prev.includes(title)
        ? prev.filter((item) => item !== title)
        : [...prev, title],
    )
  }

  if (!menuOpen) return null

  return (
    <Box className={styles.mobileNavMenu}>
      {/* Header with close button and logo - matches bee-hive */}
      <Box className={styles.mobileNavHeader}>
        <Close
          sx={{
            marginRight: '10px',
            cursor: 'pointer',
          }}
          onClick={onClose}
        />
        <HomepageLogo href="/" />
      </Box>

      {/* Menu content */}
      <Box className={styles.mobileNavContent}>
        <List>
          {navConfig.map((item) => {
            if (!item.isVisibleForUserRole) {
              return null
            }

            if (isNavDropdown(item)) {
              const isExpanded = expandedItems.includes(item.title)
              // Check if any subitem is active to highlight the parent dropdown
              const hasActiveSubItem = item.subItems.some((subItem) => {
                const linkPath = subItem.link.split('?')[0]
                return location.pathname === linkPath
              })

              return (
                <React.Fragment key={item.title}>
                  <ListItem
                    onClick={() => toggleExpanded(item.title)}
                    className={`${styles.dropdownHeader} ${hasActiveSubItem ? styles.active : ''}`}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="h6">{item.title}</Typography>
                      }
                    />
                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                  </ListItem>
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.subItems.map((subItem) => {
                        if (!subItem.isVisibleForUserRole) {
                          return null
                        }

                        // Check if this subitem is active
                        const linkPath = subItem.link.split('?')[0]
                        const isActive = location.pathname === linkPath

                        return (
                          <ListItem
                            key={subItem.link}
                            onClick={onClose}
                            className={`${styles.subMenuItem} ${isActive ? styles.active : ''}`}
                          >
                            <a
                              href={subItem.link}
                              style={{
                                textDecoration: 'none',
                                color: 'inherit',
                                width: '100%',
                              }}
                            >
                              <ListItemText
                                primary={
                                  <Typography variant="body1">
                                    {subItem.title}
                                  </Typography>
                                }
                              />
                            </a>
                          </ListItem>
                        )
                      })}
                    </List>
                  </Collapse>
                </React.Fragment>
              )
            }

            // Regular nav item (not dropdown)
            const linkPath = item.link.split('?')[0]
            const isActive = location.pathname === linkPath

            return (
              <ListItem
                key={item.title}
                onClick={onClose}
                className={`${styles.dropdownHeader} ${isActive ? styles.active : ''}`}
              >
                <a
                  href={item.link}
                  style={{
                    textDecoration: 'none',
                    color: 'inherit',
                    width: '100%',
                  }}
                >
                  <ListItemText
                    primary={<Typography variant="h6">{item.title}</Typography>}
                  />
                </a>
              </ListItem>
            )
          })}
        </List>
      </Box>
    </Box>
  )
}

export default MobileNavMenu
